import { useState, useEffect } from "react";
import { ScrollFade } from "@/components/ScrollFade";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MemberStatus, MemberCheckAlert } from "@/components/MemberStatus";
import { UserCog, DollarSign, ImageIcon, Save, Palette, Tag, Download, Power, Gift, RefreshCw, Landmark, Mic } from "lucide-react";

const eur = (c) => ((c || 0) / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
// 24 derniers mois, en toutes lettres (« novembre 2026 ») : lisible sur téléphone.
const MONTHS = Array.from({ length: 24 }, (_, i) => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - i);
  const v = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  return [v, d.toLocaleDateString("fr-FR", { month: "long", year: "numeric" })];
});

export default function Admin() {
  const [data, setData] = useState(null);
  const [limit, setLimit] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);
  const [billingMode, setBillingMode] = useState("off");
  const [aiEnabled, setAiEnabled] = useState(false);
  const [storage, setStorage] = useState(null);
  const [trialDays, setTrialDays] = useState("14");
  const [interests, setInterests] = useState([]);
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState({ code: "", type: "meeting", max_uses: 0, label: "", group: "", meeting_date: "", sponsor_code: "" });
  const [quotas, setQuotas] = useState(null);
  const [conversion, setConversion] = useState([]);
  const [affConfig, setAffConfig] = useState(null);
  const [affiliates, setAffiliates] = useState([]);
  const [exportMonth, setExportMonth] = useState("");
  const [simInfo, setSimInfo] = useState(null);
  const [simForm, setSimForm] = useState({ referred_email: "", amount_ht_eur: 29, first_year: true, aged: false });
  const [demoSteps, setDemoSteps] = useState(null);
  const [inviteDays, setInviteDays] = useState("3");
  const [graceDays, setGraceDays] = useState("30");
  const [meetings, setMeetings] = useState([]);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => {
      setLimit(String(r.data.monthly_image_limit));
      setBillingMode(r.data.billing_mode || "off");
      setAiEnabled(!!r.data.ai_illustration_enabled);
      setTrialDays(String(r.data.public_trial_days ?? 14));
      setInviteDays(String(r.data.invite_meeting_days ?? 3));
      setGraceDays(String(r.data.member_grace_days ?? 30));
    }).catch(() => {});
    api.get("/admin/meetings").then((r) => setMeetings(r.data.rows)).catch(() => {});
    api.get("/admin/members").then((r) => setMembers(r.data.rows)).catch(() => {});
    api.get("/admin/interests").then((r) => setInterests(r.data)).catch(() => {});
    api.get("/admin/codes").then((r) => setCodes(r.data)).catch(() => {});
    api.get("/admin/storage").then((r) => setStorage(r.data)).catch(() => {});
    api.get("/admin/quotas").then((r) => setQuotas(r.data.plan_quotas)).catch(() => {});
    api.get("/admin/conversion").then((r) => setConversion(r.data.rows)).catch(() => {});
    api.get("/admin/affiliate/config").then((r) => setAffConfig(r.data)).catch(() => {});
    api.get("/admin/affiliates").then((r) => setAffiliates(r.data.rows)).catch(() => {});
    api.get("/admin/affiliate/simulator").then((r) => setSimInfo(r.data)).catch(() => {});
  }, []);

  const loadSim = () => api.get("/admin/affiliate/simulator").then((r) => setSimInfo(r.data)).catch(() => {});
  const doSimulate = async () => {
    try {
      const { data } = await api.post("/admin/affiliate/simulate-payment", simForm);
      toast.success(`Encaissement simulé pour « ${data.affiliate.code} » → commission ${eur(data.commission.net_cents)} (${data.commission.status === "available" ? "disponible" : "en gel"}, TEST).`);
      loadAffiliates(); loadSim();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doPurgeTest = async () => {
    if (!window.confirm("Supprimer toutes les données de test (commissions/règlements/clics/comptes marqués TEST) ? Irréversible.")) return;
    try {
      const { data } = await api.post("/admin/affiliate/purge-test");
      toast.success(`Purge effectuée : ${data.deleted_commissions} commission(s) TEST supprimée(s).`);
      setDemoSteps(null); loadAffiliates(); loadSim();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const runDemoScenario = async () => {
    try {
      const { data } = await api.post("/admin/affiliate/demo-scenario");
      setDemoSteps(data.steps);
      toast.success(`Scénario lancé : parrain fictif « ${data.affiliate.name} » créé.`);
      loadAffiliates(); loadSim();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const loadAffiliates = () => api.get("/admin/affiliates").then((r) => setAffiliates(r.data.rows)).catch(() => {});
  const saveAffConfig = async () => {
    try {
      await api.put("/admin/affiliate/config", {
        rate_year1: affConfig.rate_year1, rate_after: affConfig.rate_after,
        gel_days: affConfig.gel_days, payout_threshold_cents: affConfig.payout_threshold_cents,
      });
      toast.success("Barème d'affiliation enregistré.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const doPayout = async (aid) => {
    try {
      const { data } = await api.post(`/admin/affiliate/${aid}/payout`, {});
      toast.success(`Règlement enregistré : ${eur(data.amount_cents)} (${data.count} commission(s), ${data.method === "credit_cotisation" ? "crédit cotisation" : "virement"}).`);
      loadAffiliates();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const exportCommissions = async () => {
    try {
      const res = await api.get("/admin/commissions/export", { params: exportMonth ? { month: exportMonth } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `commissions_${exportMonth || "toutes"}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible."); }
  };
  const runReconcile = async () => {
    try {
      const { data } = await api.post("/admin/affiliate/reconcile");
      toast.success(`Réconciliation Stripe : ${data.created} commission(s) rattrapée(s).`);
      loadAffiliates();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const createCode = async () => {
    try {
      await api.post("/admin/codes", newCode);
      toast.success("Code créé.");
      setNewCode({ code: "", type: "meeting", max_uses: 0, label: "", group: "", meeting_date: "", sponsor_code: "" });
      api.get("/admin/codes").then((r) => setCodes(r.data));
      api.get("/admin/meetings").then((r) => setMeetings(r.data.rows));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const loadMembers = () => api.get("/admin/members").then((r) => setMembers(r.data.rows)).catch(() => {});
  const setMembership = async (id, dateStr) => {
    try {
      await api.post(`/admin/user/${id}/access`, dateStr ? { membership_ends_at: new Date(dateStr + "T00:00:00").toISOString() } : { clear_membership_end: true });
      toast.success(dateStr ? "Date de fin d'adhésion enregistrée." : "Adhésion illimitée rétablie.");
      loadMembers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveAccessDurations = async () => {
    try {
      await api.put("/admin/settings", { monthly_image_limit: parseInt(limit, 10) || 0,
        public_trial_days: parseInt(trialDays, 10) || 0, invite_meeting_days: parseInt(inviteDays, 10) || 0,
        member_grace_days: parseInt(graceDays, 10) || 0 });
      toast.success("Durées d'accès mises à jour.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deactivateCode = async (id) => {
    await api.delete(`/admin/codes/${id}`);
    api.get("/admin/codes").then((r) => setCodes(r.data));
  };
  const saveQuotas = async () => {
    try { await api.put("/admin/quotas", { plan_quotas: quotas }); toast.success("Quotas enregistrés."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setQ = (plan, key, val) => setQuotas((q) => ({ ...q, [plan]: { ...q[plan], [key]: parseInt(val, 10) || 0 } }));

  const saveLimit = async () => {
    setSavingLimit(true);
    try {
      await api.put("/admin/settings", { monthly_image_limit: parseInt(limit, 10) || 0 });
      toast.success("Limite mensuelle d'illustrations mise à jour.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingLimit(false);
    }
  };

  const saveTrialDays = async () => {
    try {
      await api.put("/admin/settings", { monthly_image_limit: parseInt(limit, 10) || 0, public_trial_days: parseInt(trialDays, 10) || 0 });
      toast.success("Durée d'essai public mise à jour.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setMode = async (mode) => {
    const prev = billingMode;
    setBillingMode(mode);
    try {
      await api.put("/admin/settings", { monthly_image_limit: parseInt(limit, 10) || 0, billing_mode: mode });
      toast.success(`Facturation : mode « ${mode} » activé.`);
    } catch (err) {
      setBillingMode(prev);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setAiIllustration = async (next) => {
    const prev = aiEnabled;
    setAiEnabled(next);
    try {
      await api.put("/admin/settings", { monthly_image_limit: parseInt(limit, 10) || 0, ai_illustration_enabled: next });
      toast.success(next ? "Illustration IA activée." : "Illustration IA désactivée.");
    } catch (err) {
      setAiEnabled(prev);
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/interests/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "interets_pulse.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export impossible."); }
  };

  const setPlan = async (uid, plan) => {
    await api.put(`/admin/users/${uid}/plan`, { plan: plan || null });
    toast.success("Plan mis à jour.");
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
  };

  const [kits, setKits] = useState([]);
  useEffect(() => { api.get("/admin/brand-kits").then((r) => setKits(r.data)).catch(() => {}); }, []);
  const reloadAll = () => {
    api.get("/admin/overview").then((r) => setData(r.data)).catch(() => {});
    api.get("/admin/brand-kits").then((r) => setKits(r.data)).catch(() => {});
  };
  const seedApollo = async () => {
    await api.post("/admin/seed-apollo-kit");
    toast.success("Kit APOLLO préchargé.");
    reloadAll();
  };
  const assignKit = async (uid, kitId) => {
    await api.post(`/admin/users/${uid}/assign-kit`, { org_kit_id: kitId || null });
    toast.success("Kit d'organisation assigné.");
    reloadAll();
  };

  if (!data) return <div className="p-12 text-slate-400">Chargement…</div>;

  return (
    <div className="p-8 lg:p-12">
      <MemberCheckAlert />
      <div className="mb-2 flex items-center gap-2 text-coral-dark">
        <UserCog className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-[0.2em]">Back-office</span>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-serif text-4xl text-slate-900">Administration</h1>
        <Link to="/admin/conferences" data-testid="admin-conferences-link" className="inline-flex items-center gap-2 text-sm font-semibold border border-slate-300 px-4 py-2 rounded-sm hover:border-coral hover:text-coral-dark transition-colors"><Mic className="h-4 w-4" /> Conférences APOLLO</Link>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
        <div className="bg-white border border-slate-200 rounded-sm p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-2">Comptes</p>
          <p className="font-serif text-3xl text-slate-900">{data.user_count}</p>
        </div>
        <div className="bg-ink text-white rounded-lg p-6">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-coral mb-2 flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Coût API total</p>
          <p className="font-serif text-3xl">${data.total_cost_usd.toFixed(4)}</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-xl">
        <div className="flex items-center gap-2 text-coral-dark mb-1">
          <ImageIcon className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-[0.15em]">Protection des coûts</span>
        </div>
        <p className="font-serif text-xl text-ink mb-3">Limite d'illustrations IA / utilisateur / mois</p>
        <div className="flex items-center gap-3">
          <input data-testid="image-limit-input" type="number" min="0" value={limit} onChange={(e) => setLimit(e.target.value)}
            className="w-32 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
          <button data-testid="save-limit-btn" onClick={saveLimit} disabled={savingLimit}
            className="flex items-center gap-2 bg-coral-strong text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors disabled:opacity-60">
            <Save className="h-4 w-4" /> Enregistrer
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-2">S'applique à tous les comptes tant que la facturation n'est pas en mode « Actif ». Un visuel coûte environ 0,40 € : c'est le seul levier qui protège réellement votre facture pendant le pilote.</p>
      </div>

      {/* Interrupteur facturation */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-2xl" data-testid="billing-switch">
        <div className="flex items-center gap-2 text-coral-dark mb-1">
          <Power className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-[0.15em]">Facturation</span>
        </div>
        <p className="font-serif text-xl text-ink mb-1">Mode d'affichage des offres</p>
        <p className="text-xs text-slate-400 mb-4">La mise en production commerciale (mode « actif ») n'active le paiement réel qu'après votre validation.</p>
        <div className="grid grid-cols-3 gap-2">
          {[
            ["off", "Désactivé", "Aucun écran tarifaire"],
            ["demo", "Démo", "Tarifs visibles, « Je suis intéressé »"],
            ["active", "Actif", "Stripe réel"],
          ].map(([v, label, desc]) => (
            <button key={v} data-testid={`billing-mode-${v}`} onClick={() => setMode(v)}
              className={`text-left rounded-sm border p-3 transition-colors ${billingMode === v ? "border-coral bg-coral-soft" : "border-slate-200 hover:border-coral"}`}>
              <p className={`text-sm font-bold ${billingMode === v ? "text-coral-dark" : "text-ink"}`}>{label}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Interrupteur Illustration IA (feature flag qualité) */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-2xl" data-testid="ai-illustration-switch">
        <div className="flex items-center gap-2 text-coral-dark mb-1">
          <Power className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-[0.15em]">Illustration IA</span>
        </div>
        <p className="font-serif text-xl text-ink mb-1">Mode « Illustration IA » du Studio</p>
        <p className="text-xs text-slate-400 mb-4">Ce bouton ne concerne QUE les styles avancés du Studio créatif (photo d'objet, 3D douce, éditorial). Le mode « Image du post », celui qu'utilisent l'Atelier et le Mode rapide, reste actif dans tous les cas.</p>
        <div className="flex items-center gap-3">
          <button data-testid="ai-illustration-toggle" onClick={() => setAiIllustration(!aiEnabled)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${aiEnabled ? "bg-coral-strong" : "bg-slate-300"}`}>
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${aiEnabled ? "translate-x-6" : "translate-x-1"}`} />
          </button>
          <span className={`text-sm font-bold ${aiEnabled ? "text-coral-dark" : "text-slate-500"}`}>{aiEnabled ? "Activé" : "Désactivé"}</span>
        </div>
      </div>

      {/* Stockage fichiers & médias (anticipation des coûts) */}
      {storage && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-3xl" data-testid="storage-card">
          <div className="flex items-center gap-2 text-coral-dark mb-1"><Power className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Stockage fichiers & médias</span></div>
          <p className="font-serif text-xl text-ink mb-4">{storage.total_mb} Mo · {storage.total_files} fichiers · {storage.users.length} utilisateurs</p>
          <div className="max-h-72 overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 text-xs uppercase tracking-wider"><th className="py-1">Utilisateur</th><th className="py-1 text-right">Fichiers</th><th className="py-1 text-right">Mo</th></tr></thead>
              <tbody>
                {storage.users.map((u) => (
                  <tr key={u.user_id} className="border-t border-slate-100" data-testid="storage-row">
                    <td className="py-1.5 text-ink">{u.email}</td>
                    <td className="py-1.5 text-right text-ink-light">{u.files}</td>
                    <td className="py-1.5 text-right font-semibold text-ink">{u.mb}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-3xl" data-testid="access-config-card">
        <div className="flex items-center gap-2 text-coral-dark mb-3"><Power className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Durées d'accès (jours)</span></div>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Invité de réunion</label>
            <input data-testid="invite-days-input" type="number" min="0" value={inviteDays} onChange={(e) => setInviteDays(e.target.value)}
              className="mt-1.5 block w-24 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Essai public (sans CB)</label>
            <input data-testid="trial-days-input" type="number" min="0" value={trialDays} onChange={(e) => setTrialDays(e.target.value)}
              className="mt-1.5 block w-24 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Grâce fin d'adhésion</label>
            <input data-testid="grace-days-input" type="number" min="0" value={graceDays} onChange={(e) => setGraceDays(e.target.value)}
              className="mt-1.5 block w-24 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
          </div>
          <button data-testid="save-access-days-btn" onClick={saveAccessDurations} className="bg-coral-strong text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors">Enregistrer</button>
        </div>
        <p className="text-xs text-slate-400 mt-2">Invité 3 j → bascule automatique en essai public. Membre : accès complet, puis grâce avant lecture seule.</p>
      </div>

      {/* Intérêts (mode démo) */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-3xl" data-testid="interests-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 text-coral-dark mb-1">
              <Tag className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-[0.15em]">Prospects (mode démo)</span>
            </div>
            <p className="font-serif text-xl text-ink">Manifestations d'intérêt</p>
          </div>
          <button data-testid="export-interests-btn" onClick={exportCsv} disabled={interests.length === 0}
            className="flex items-center gap-2 text-sm font-semibold text-coral-dark border border-coral px-3 py-2 rounded-sm hover:bg-coral-strong hover:text-white transition-colors disabled:opacity-40">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
        {interests.length === 0 ? (
          <p className="text-sm text-slate-400">Aucune manifestation d'intérêt pour l'instant.</p>
        ) : (
          <ScrollFade>
            <table className="w-full text-sm" data-testid="interests-table">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-4">E-mail</th><th className="py-2 pr-4">Plan visé</th><th className="py-2 pr-4">Parrain</th><th className="py-2">Date</th>
                </tr>
              </thead>
              <tbody>
                {interests.map((it) => (
                  <tr key={it.id} className="border-b border-slate-50">
                    <td className="py-2 pr-4 text-slate-700">{it.email}</td>
                    <td className="py-2 pr-4"><span className="text-xs font-semibold bg-coral-soft text-coral-dark px-2 py-0.5 rounded-full">{it.plan}</span></td>
                    <td className="py-2 pr-4 text-slate-500">{it.ref_code || ""}</td>
                    <td className="py-2 text-slate-400 text-xs">{(it.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollFade>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-10 max-w-2xl">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 text-coral-dark mb-1">
              <Palette className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-[0.15em]">Kits d'organisation</span>
            </div>
            <p className="font-display font-bold text-xl text-ink">Marques préchargées (offre Équipe)</p>
          </div>
          <button data-testid="seed-apollo-btn" onClick={seedApollo}
            className="text-sm font-semibold text-coral-dark border border-coral px-3 py-2 rounded-lg hover:bg-coral hover:text-white transition-colors">
            Précharger le kit APOLLO
          </button>
        </div>
        {kits.length === 0 ? (
          <p className="text-sm text-slate-400">Aucun kit d'organisation. Préchargez le kit APOLLO pour l'équipe.</p>
        ) : (
          <div className="flex flex-wrap gap-3" data-testid="org-kits-list">
            {kits.map((k) => (
              <div key={k.id} className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2">
                <span className="w-5 h-5 rounded-full border border-slate-200" style={{ background: k.primary_color }} />
                <span className="text-sm font-semibold text-ink">{k.org_name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Codes de réunion & Membre */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-3xl" data-testid="codes-card">
        <div className="flex items-center gap-2 text-coral-dark mb-1">
          <Tag className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-[0.15em]">Codes d'activation</span>
        </div>
        <p className="font-serif text-xl text-ink mb-4">Codes de réunion</p>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <input data-testid="code-code-input" value={newCode.code} onChange={(e) => setNewCode({ ...newCode, code: e.target.value })}
            placeholder="CODE" className="border border-slate-200 rounded-sm px-3 py-2 text-sm uppercase w-44 outline-none focus:ring-1 focus:ring-coral" />
          <input type="number" title="Utilisations max (0 = illimité)" value={newCode.max_uses} onChange={(e) => setNewCode({ ...newCode, max_uses: parseInt(e.target.value, 10) || 0 })}
            className="border border-slate-200 rounded-sm px-2 py-2 text-sm w-24 outline-none focus:ring-1 focus:ring-coral" placeholder="max" />
        </div>
        {newCode.type === "meeting" && (
          <div className="flex flex-wrap items-end gap-2 mb-3">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Libellé</label>
              <input data-testid="code-label-input" value={newCode.label} onChange={(e) => setNewCode({ ...newCode, label: e.target.value })}
                placeholder="Réunion Paris" className="mt-1 block border border-slate-200 rounded-sm px-3 py-2 text-sm w-44 outline-none focus:ring-1 focus:ring-coral" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Groupe</label>
              <input data-testid="code-group-input" value={newCode.group} onChange={(e) => setNewCode({ ...newCode, group: e.target.value })}
                placeholder="Groupe DR-75" className="mt-1 block border border-slate-200 rounded-sm px-3 py-2 text-sm w-40 outline-none focus:ring-1 focus:ring-coral" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Date de réunion</label>
              <input data-testid="code-date-input" type="date" value={newCode.meeting_date} onChange={(e) => setNewCode({ ...newCode, meeting_date: e.target.value })}
                className="mt-1 block border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
            </div>
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Code parrain (optionnel)</label>
              <input data-testid="code-sponsor-input" value={newCode.sponsor_code} onChange={(e) => setNewCode({ ...newCode, sponsor_code: e.target.value })}
                placeholder="code affilié" className="mt-1 block border border-slate-200 rounded-sm px-3 py-2 text-sm w-40 outline-none focus:ring-1 focus:ring-coral" />
            </div>
          </div>
        )}
        <button data-testid="create-code-btn" onClick={createCode}
          className="bg-coral-strong text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors">Créer le code</button>
        {codes.length > 0 && (
          <div className="space-y-1.5 mt-4" data-testid="codes-list">
            {codes.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-sm px-3 py-2">
                <span className="font-mono font-bold text-ink">{c.code}</span>
                <span className="text-xs text-slate-500">
                  {c.type === "member" ? "Membre" : `Réunion ${c.meeting_date || ""}${c.label ? " · " + c.label : ""}`}
                  {c.sponsor_code ? ` · parrain ${c.sponsor_code}` : ""} · {c.uses}{c.max_uses ? `/${c.max_uses}` : ""} util. · {c.active ? "actif" : "inactif"}
                </span>
                {c.active && <button data-testid={`deactivate-code-${c.id}`} onClick={() => deactivateCode(c.id)} className="text-xs text-destructive hover:underline">Désactiver</button>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vue par réunion (conversion) */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-5xl" data-testid="meetings-card">
        <div className="flex items-center gap-2 text-coral-dark mb-1"><Tag className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Canal réunions</span></div>
        <p className="font-serif text-xl text-ink mb-4">Conversion par réunion</p>
        {meetings.length === 0 ? (
          <p className="text-sm text-slate-400">Aucune réunion pour l'instant.</p>
        ) : (
          <ScrollFade>
            <table className="w-full text-sm" data-testid="meetings-table">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-4">Réunion</th><th className="py-2 pr-4">Date</th><th className="py-2 pr-4 text-right">Invités</th>
                  <th className="py-2 pr-4 text-right">Membres</th><th className="py-2 pr-4 text-right">Clients</th>
                  <th className="py-2 pr-4 text-right">→ Membre</th><th className="py-2 text-right">→ Client</th>
                </tr>
              </thead>
              <tbody>
                {meetings.map((m) => (
                  <tr key={m.code} className="border-b border-slate-50">
                    <td className="py-2.5 pr-4"><span className="font-mono text-xs text-slate-500">{m.code}</span> <span className="text-slate-700">{m.label || ""}</span>{m.group ? <span className="text-xs text-slate-400"> · {m.group}</span> : ""}</td>
                    <td className="py-2.5 pr-4 text-slate-400 text-xs">{m.meeting_date}</td>
                    <td className="py-2.5 pr-4 text-right">{m.invited}</td>
                    <td className="py-2.5 pr-4 text-right">{m.members}</td>
                    <td className="py-2.5 pr-4 text-right">{m.clients}</td>
                    <td className="py-2.5 pr-4 text-right font-semibold text-coral-dark">{m.rate_member}%</td>
                    <td className="py-2.5 text-right font-semibold text-green-700">{m.rate_client}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollFade>
        )}
      </div>

      {/* Membres APOLLO — fin d'adhésion */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-4xl" data-testid="members-card">
        <div className="flex items-center gap-2 text-coral-dark mb-1"><UserCog className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Membres APOLLO</span></div>
        <p className="font-serif text-xl text-ink mb-4">Gestion des fins d'adhésion</p>
        {members.length === 0 ? (
          <p className="text-sm text-slate-400">Aucun Membre APOLLO pour l'instant.</p>
        ) : (
          <ScrollFade>
            <table className="w-full text-sm" data-testid="members-table">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-4">Membre</th><th className="py-2 pr-4">État</th><th className="py-2 pr-4">Fin d'adhésion</th><th className="py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-slate-50">
                    <td className="py-2.5 pr-4"><p className="font-semibold text-slate-800">{m.name}</p><p className="text-xs text-slate-400">{m.email}</p></td>
                    <td className="py-2.5 pr-4">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${m.state === "member_grace" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-green-50 text-green-700 border-green-200"}`}>
                        {m.state === "member_grace" ? "En grâce" : "Actif"}
                      </span>
                    </td>
                    <td className="py-2.5 pr-4">
                      <input data-testid={`member-date-${m.id}`} type="date" defaultValue={(m.membership_ends_at || "").slice(0, 10)}
                        onBlur={(e) => e.target.value !== (m.membership_ends_at || "").slice(0, 10) && setMembership(m.id, e.target.value)}
                        className="border border-slate-200 rounded-sm px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-coral" />
                    </td>
                    <td className="py-2.5">
                      {m.membership_ends_at && <button data-testid={`member-clear-${m.id}`} onClick={() => setMembership(m.id, "")} className="text-xs text-slate-500 hover:text-coral-dark hover:underline">Illimité</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollFade>
        )}
      </div>

      {/* Quotas par plan */}
      {quotas && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-3xl" data-testid="quotas-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 text-coral-dark mb-1"><ImageIcon className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Quotas par plan / mois</span></div>
              <p className="font-serif text-xl text-ink">Illustrations · Textes · Posts programmés</p>
              <p className="text-xs text-slate-500 mt-1">Un visuel IA coûte environ 0,40 € : c'est « img » qui pilote votre facture, pas « txt ».</p>
            </div>
            <button data-testid="save-quotas-btn" onClick={saveQuotas} className="flex items-center gap-2 bg-coral-strong text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors"><Save className="h-4 w-4" /> Enregistrer</button>
          </div>
          <div className="space-y-2">
            {["invite", "member", "solo", "signature"].map((plan) => (
              <div key={plan} className="flex flex-wrap items-center gap-3">
                <span className="w-24 text-sm font-semibold text-ink capitalize">{plan}</span>
                {["images", "texts", "scheduled", "per_gen"].map((k) => (
                  <label key={k} className="flex items-center gap-1.5 text-xs text-slate-500">
                    {k === "images" ? "img" : k === "texts" ? "txt" : k === "scheduled" ? "prog" : "img/clic"}
                    <input data-testid={`quota-${plan}-${k}`} type="number" min={k === "per_gen" ? "1" : "0"} max={k === "per_gen" ? "4" : undefined} value={quotas[plan]?.[k] ?? (k === "per_gen" ? 2 : 0)} onChange={(e) => setQ(plan, k, e.target.value)}
                      className="w-16 border border-slate-200 rounded-sm px-2 py-1 outline-none focus:ring-1 focus:ring-coral" />
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conversion essai -> payant */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-3xl" data-testid="conversion-card">
        <div className="flex items-center gap-2 text-coral-dark mb-1"><DollarSign className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Conversion essai → payant</span></div>
        <p className="font-serif text-xl text-ink mb-4">Par cohorte &amp; par source</p>
        {conversion.length === 0 ? (
          <p className="text-sm text-slate-400">Pas encore de données de conversion.</p>
        ) : (
          <table className="w-full text-sm" data-testid="conversion-table">
            <thead><tr className="text-left text-xs uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100"><th className="py-2">Cohorte</th><th className="py-2">Source</th><th className="py-2 text-right">Essais</th><th className="py-2 text-right">Convertis</th><th className="py-2 text-right">Taux</th></tr></thead>
            <tbody>
              {conversion.map((r, i) => (
                <tr key={i} className="border-b border-slate-50">
                  <td className="py-2 text-slate-700">{r.cohort}</td><td className="py-2"><span className="text-xs font-semibold bg-slate-100 px-2 py-0.5 rounded-full">{r.source}</span></td>
                  <td className="py-2 text-right">{r.trials}</td><td className="py-2 text-right">{r.converted}</td><td className="py-2 text-right font-bold text-coral-dark">{r.rate}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Affiliation — barème & configuration */}
      {affConfig && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6 max-w-3xl" data-testid="affiliate-config-card">
          <div className="flex items-center gap-2 text-coral-dark mb-1"><Gift className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Affiliation</span></div>
          <p className="font-serif text-xl text-ink mb-1">Barème &amp; règles de commission</p>
          <p className="text-xs text-slate-400 mb-4">Commission sur le montant HT encaissé. Gel avant disponibilité. Seuil de déclenchement du virement.</p>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Taux 1re année (%)</label>
              <input data-testid="aff-rate-year1" type="number" min="0" max="100" value={Math.round((affConfig.rate_year1 || 0) * 100)}
                onChange={(e) => setAffConfig({ ...affConfig, rate_year1: (parseFloat(e.target.value) || 0) / 100 })}
                className="mt-1.5 block w-24 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Taux ensuite (%)</label>
              <input data-testid="aff-rate-after" type="number" min="0" max="100" value={Math.round((affConfig.rate_after || 0) * 100)}
                onChange={(e) => setAffConfig({ ...affConfig, rate_after: (parseFloat(e.target.value) || 0) / 100 })}
                className="mt-1.5 block w-24 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Gel (jours)</label>
              <input data-testid="aff-gel-days" type="number" min="0" value={affConfig.gel_days}
                onChange={(e) => setAffConfig({ ...affConfig, gel_days: parseInt(e.target.value, 10) || 0 })}
                className="mt-1.5 block w-24 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Seuil virement (€ HT)</label>
              <input data-testid="aff-threshold" type="number" min="0" value={Math.round((affConfig.payout_threshold_cents || 0) / 100)}
                onChange={(e) => setAffConfig({ ...affConfig, payout_threshold_cents: (parseInt(e.target.value, 10) || 0) * 100 })}
                className="mt-1.5 block w-28 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
            </div>
            <button data-testid="save-aff-config-btn" onClick={saveAffConfig} className="flex items-center gap-2 bg-coral-strong text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors"><Save className="h-4 w-4" /> Enregistrer</button>
          </div>
        </div>
      )}

      {/* Simulateur d'encaissement (démo/sandbox) */}
      {simInfo && (
        <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6 max-w-3xl" data-testid="simulator-card">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 text-coral-dark"><Gift className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Simulateur d'encaissement (démo)</span></div>
            <span data-testid="simulator-test-count" className="text-xs text-slate-400">{simInfo.test_commissions} commission(s) TEST</span>
          </div>
          <p className="font-serif text-xl text-ink mb-1">Rejouer un paiement de test</p>
          <p className="text-xs text-slate-400 mb-4">Génère une commission marquée <strong>TEST</strong> (exclue des totaux réels et jamais réglée en argent). Pour démontrer le flux complet aux associés.</p>
          {!simInfo.enabled ? (
            <div data-testid="simulator-disabled" className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-sm text-amber-800">
              Simulateur désactivé : facturation active avec clés Stripe live.
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">E-mail du filleul</label>
                <input data-testid="sim-email-input" value={simForm.referred_email} onChange={(e) => setSimForm({ ...simForm, referred_email: e.target.value })}
                  placeholder="filleul@exemple.fr" className="mt-1.5 block w-64 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Montant HT (€)</label>
                <input data-testid="sim-amount-input" type="number" min="0" value={simForm.amount_ht_eur} onChange={(e) => setSimForm({ ...simForm, amount_ht_eur: parseFloat(e.target.value) || 0 })}
                  className="mt-1.5 block w-28 border border-slate-200 rounded-sm px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-coral" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 pb-2">
                <input data-testid="sim-firstyear-toggle" type="checkbox" checked={simForm.first_year} onChange={(e) => setSimForm({ ...simForm, first_year: e.target.checked })} /> 1re année (50%)
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-600 pb-2">
                <input data-testid="sim-aged-toggle" type="checkbox" checked={simForm.aged} onChange={(e) => setSimForm({ ...simForm, aged: e.target.checked })} /> Gel écoulé (disponible)
              </label>
              <button data-testid="simulate-btn" onClick={doSimulate}
                className="bg-coral-strong text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-coral-hover transition-colors">Simuler l'encaissement</button>
            </div>
          )}
          {simInfo.enabled && (
            <div className="mt-5 pt-5 border-t border-slate-100" data-testid="demo-scenario-block">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold text-ink text-sm">Scénario guidé pour la démonstration</p>
                  <p className="text-xs text-slate-400">Crée un parrain + un filleul fictifs (TEST), simule 3 mois d'encaissements (gel → disponible), commenté pas à pas.</p>
                </div>
                <button data-testid="demo-scenario-btn" onClick={runDemoScenario}
                  className="bg-ink text-white px-4 py-2 rounded-sm text-sm font-semibold hover:bg-ink/90 transition-colors shrink-0">▶ Lancer le scénario de démonstration</button>
              </div>
              {demoSteps && (
                <div className="mt-4 space-y-2" data-testid="demo-scenario-steps">
                  {demoSteps.map((s, i) => (
                    <div key={i} className={`rounded-sm border px-4 py-3 ${s.status === "summary" ? "bg-coral-soft border-coral/30" : s.status === "available" ? "bg-green-50 border-green-200" : "bg-amber-50 border-amber-200"}`}>
                      <p className="text-sm font-semibold text-ink">{s.titre}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{s.detail}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-slate-100">
            <button data-testid="purge-test-btn" onClick={doPurgeTest} disabled={simInfo.test_commissions === 0}
              className="flex items-center gap-2 text-sm font-semibold text-destructive border border-red-200 px-3 py-2 rounded-sm hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <RefreshCw className="h-4 w-4" /> Purger les données de test (avant ouverture commerciale)
            </button>
          </div>
        </div>
      )}

      {/* Affiliation — affiliés & réconciliation */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-10 max-w-5xl" data-testid="affiliates-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-coral-dark mb-1"><Landmark className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-[0.15em]">Affiliés &amp; commissions</span></div>
            <p className="font-serif text-xl text-ink">Réconciliation Stripe &amp; règlements</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="export-month-input" value={exportMonth} onChange={(e) => setExportMonth(e.target.value)}
              aria-label="Mois de l'export"
              className="border border-slate-200 rounded-sm px-2 py-2 text-sm bg-white outline-none focus:ring-1 focus:ring-coral">
              <option value="">Tous les mois</option>
              {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button data-testid="export-commissions-btn" onClick={exportCommissions}
              className="flex items-center gap-2 text-sm font-semibold text-coral-dark border border-coral px-3 py-2 rounded-sm hover:bg-coral-strong hover:text-white transition-colors"><Download className="h-4 w-4" /> Export CSV</button>
            <button data-testid="reconcile-btn" onClick={runReconcile}
              className="flex items-center gap-2 text-sm font-semibold text-slate-600 border border-slate-200 px-3 py-2 rounded-sm hover:border-coral hover:text-coral-dark transition-colors"><RefreshCw className="h-4 w-4" /> Réconcilier</button>
          </div>
        </div>
        {affiliates.length === 0 ? (
          <p className="text-sm text-slate-400">Aucun affilié avec activité pour l'instant.</p>
        ) : (
          <ScrollFade>
            <table className="w-full text-sm" data-testid="affiliates-table">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-4">Affilié</th><th className="py-2 pr-4">Code</th>
                  <th className="py-2 pr-4 text-right">Inscrits</th><th className="py-2 pr-4 text-right">Actifs</th>
                  <th className="py-2 pr-4 text-right">Estimées</th><th className="py-2 pr-4 text-right">Disponibles</th>
                  <th className="py-2 pr-4 text-right">Versées</th><th className="py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.map((a) => (
                  <tr key={a.affiliate_id} className="border-b border-slate-50">
                    <td className="py-2.5 pr-4">
                      <p className="font-semibold text-slate-800">{a.name} {a.is_member && <span className="text-[10px] font-bold bg-coral-soft text-coral-dark px-1.5 py-0.5 rounded-full">Membre</span>}</p>
                      <p className="text-xs text-slate-400">{a.email}{a.test_cents > 0 && <span className="ml-2 text-slate-500">· dont {eur(a.test_cents)} TEST (exclu)</span>}</p>
                    </td>
                    <td className="py-2.5 pr-4 font-mono text-xs text-slate-500">{a.code}</td>
                    <td className="py-2.5 pr-4 text-right">{a.signups}</td>
                    <td className="py-2.5 pr-4 text-right">{a.active_clients}</td>
                    <td className="py-2.5 pr-4 text-right text-amber-600">{eur(a.pending_cents)}</td>
                    <td className="py-2.5 pr-4 text-right font-semibold text-green-700">{eur(a.available_cents)}</td>
                    <td className="py-2.5 pr-4 text-right text-slate-500">{eur(a.paid_cents)}</td>
                    <td className="py-2.5">
                      <button data-testid={`payout-${a.affiliate_id}`} onClick={() => doPayout(a.affiliate_id)} disabled={a.available_cents <= 0}
                        className="text-xs font-semibold text-white bg-ink px-3 py-1.5 rounded-sm hover:bg-ink/90 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        {a.is_member ? "Créditer" : "Régler"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollFade>
        )}
      </div>

      <ScrollFade className="bg-white border border-slate-200 rounded-lg">
        <table className="w-full text-sm" data-testid="admin-users-table">
          <thead>
            <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">
              <th className="px-6 py-3">Utilisateur</th>
              <th className="px-6 py-3">Rôle</th>
              <th className="px-6 py-3">Kit d'organisation</th>
              <th className="px-6 py-3">Plan</th>
              <th className="px-6 py-3">Membre APOLLO</th>
              <th className="px-6 py-3 text-right">Générations</th>
              <th className="px-6 py-3 text-right">Coût API</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-6 py-4">
                  <p className="font-semibold text-slate-900">{u.name}</p>
                  <p className="text-slate-400 text-xs">{u.email}</p>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${u.role === "admin" ? "bg-coral-soft text-coral-dark" : "bg-slate-100 text-slate-600"}`}>
                    {u.role === "admin" ? "Admin" : "Utilisateur"}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <select data-testid={`assign-kit-${u.id}`} value={u.org_kit_id || ""} onChange={(e) => assignKit(u.id, e.target.value)}
                    className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-coral">
                    <option value="">Aucun (kit perso)</option>
                    {kits.map((k) => <option key={k.id} value={k.id}>{k.org_name}</option>)}
                  </select>
                </td>
                <td className="px-6 py-4">
                  <select data-testid={`set-plan-${u.id}`} value={u.plan || ""} onChange={(e) => setPlan(u.id, e.target.value)}
                    className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs outline-none focus:ring-1 focus:ring-coral">
                    <option value="">Aucun</option>
                    <option value="member">Membre APOLLO</option>
                    <option value="solo">Solo</option>
                    <option value="invite">Essai Invité</option>
                  </select>
                </td>
                <td className="px-6 py-4"><MemberStatus u={u} onChanged={(nu) => setData((d) => ({ ...d, users: d.users.map((x) => (x.id === nu.id ? nu : x)) }))} /></td>
                <td className="px-6 py-4 text-right text-slate-700">{u.generations}</td>
                <td className="px-6 py-4 text-right font-semibold text-coral-dark">${u.cost_usd.toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollFade>
    </div>
  );
}
